/**
 * System Settings → Users — the super-admin user management wire contract.
 *
 * Served by `apps/api/src/routes/platform/users.ts` at `/api/v1/users`
 * (super admins only, `requireSuperAdmin()`), over the three SECURITY DEFINER
 * RPCs of `20261027200000_super_admin_users_modernize.sql`:
 *
 *   GET  /api/v1/users
 *       → `{ users: SuperAdminUserRow[] }` (`SuperAdminUsersResponse`),
 *         `get_user_admin_list()` passed through, each row's `organizations`
 *         jsonb narrowed by {@link superAdminUserOrganizationSchema}.
 *   POST /api/v1/users/:userId/super-admin   body `{ is_super_admin }`
 *       → `{ ok: true }` (`ToggleSuperAdminResponse`),
 *         `toggle_user_super_admin(target_user_id, new_status)`. The RPC is
 *         the guard (self-demotion, unknown user, the advisory-lock
 *         serialization, no grant to a deactivated user) and writes the
 *         `platform_user_audit` row; its RAISE texts reach the client
 *         verbatim as a 400.
 *   GET  /api/v1/users/:userId/responsibilities
 *       → `{ organizations: UserResponsibilityOrganization[] }`
 *         (`UserResponsibilitiesResponse`), `list_user_responsibilities` —
 *         what the user looks after, per organization, with who may take it
 *         over. Shown before a deactivate / delete.
 *   POST /api/v1/users/:userId/status        body `{ status, successors? }`
 *       → `{ ok: true, keycloak }` (`SetUserStatusResponse`),
 *         `set_user_status(target_user_id, new_status, successors)` —
 *         deactivate, reactivate or delete, handing the user's
 *         responsibilities to one successor per organization (`successors`:
 *         `{ [organization_id]: user_id }`; required on delete for every
 *         organization with a transferable item, optional on deactivate, not
 *         allowed on reactivate). The RPC is the guard (self, a super-admin
 *         target, the last admin / member of an org, an ineligible or missing
 *         successor) and writes the `platform_user_audit` row; only then does
 *         the route mirror the account's enabled flag into Keycloak
 *         (`set-keycloak-user-enabled`), and `keycloak` says how that went.
 *
 * The row type is derived from the generated RPC return type. Supabase's
 * generator types every `RETURNS TABLE` column as non-null, so the columns
 * that can be NULL in `auth.users` / the `profiles` LEFT JOIN are widened
 * here — the web must handle "never signed in" and "no profile". It types
 * `jsonb` as `Json`, so `organizations` is replaced by its parsed shape, and
 * `text` as `string`, so `status` is narrowed to {@link SuperAdminUserStatus}.
 */
import { z } from "zod";
/**
 * One element of a row's `organizations` — one membership, as the RPC builds
 * it (`jsonb_build_object` over `organization_members` ⨝ `organizations`,
 * ordered by name). `joined_at` is the membership's `created_at` instant.
 */
export const superAdminUserOrganizationSchema = z.object({
    organization_id: z.string().uuid(),
    name: z.string(),
    role: z.string(),
    joined_at: z.string(),
});
/** `POST /api/v1/users/:userId/super-admin` body — the target's new status. */
export const toggleSuperAdminBodySchema = z
    .object({
    is_super_admin: z.boolean(),
})
    .strict();
/** The statuses `set_user_status` sets — `'deleted'` is terminal. */
export const SET_USER_STATUSES = ["active", "deactivated", "deleted"];
/**
 * The most organizations one `successors` map may name — `set_user_status`
 * refuses more with the same limit (22023). A bound on the request, far above
 * any real membership count.
 */
export const MAX_USER_STATUS_SUCCESSORS = 500;
/**
 * `POST /api/v1/users/:userId/status` body — the target's new status and,
 * for a deactivate / delete, who takes over their responsibilities in each
 * organization (`{ [organization_id]: successor_user_id }`; omitted = `{}`;
 * at most {@link MAX_USER_STATUS_SUCCESSORS} keys). The RPC validates the
 * map against `list_user_responsibilities`' rules, and accepts only
 * lowercase uuids (as Postgres prints them — the web sends them back as read).
 */
export const setUserStatusBodySchema = z
    .object({
    status: z.enum(SET_USER_STATUSES),
    successors: z
        .record(z.string().uuid(), z.string().uuid())
        .refine((m) => Object.keys(m).length <= MAX_USER_STATUS_SUCCESSORS, {
        message: `successors may name at most ${MAX_USER_STATUS_SUCCESSORS} organizations`,
    })
        .optional(),
})
    .strict();
/**
 * The outcomes the `set-keycloak-user-enabled` edge function itself answers
 * (`{ status }` on a 200). The API's response schema enums over these; the
 * Deno function keeps its own `KeycloakEnabledStatus` twin (edge functions
 * cannot import the workspace package) — keep the two in step.
 */
export const KEYCLOAK_EDGE_OUTCOMES = ["updated", "not_configured", "no_keycloak_identity"];
// ---------------------------------------------------------------------------
// The user handover — GET /api/v1/users/:userId/responsibilities
// ---------------------------------------------------------------------------
/**
 * What a user can be responsible for — the kinds of
 * `_user_responsibility_items` (20261027200000). KEEP IN STEP with that
 * function's arms and with supabase/tests/manual/user_responsibility_coverage.sql
 * (docs/USER_ROLES_AND_PERMISSIONS.md "Adding a responsibility kind").
 *
 *   recurring_template    a recurring document template it runs as (accounting)
 *   stripe_channel        a Stripe sales channel it books as (accounting)
 *   volunteer_lead        a volunteer role it leads (membership)
 *   gdpr_request          an open data subject request assigned to it (admin)
 *   interaction_followup  an interaction whose follow-up reminders it gets (membership)
 *   survey_digest         a survey whose response digest it gets (surveys)
 *   bank_connection       a bank feed authorised with its own bank login — not transferable
 *   inbox                 a verified inbox forwarding alias — not transferable
 */
export const USER_RESPONSIBILITY_KINDS = [
    "recurring_template",
    "stripe_channel",
    "volunteer_lead",
    "gdpr_request",
    "interaction_followup",
    "survey_digest",
    "bank_connection",
    "inbox",
];
/**
 * Per-kind extras (`detail`, NULL for the other kinds):
 *   bank_connection  `valid_until` — when the bank consent expires (instant; may be null)
 *   gdpr_request     `deadline_at` — the request's legal deadline (instant)
 *   inbox            `unprocessed_count` — unread / read messages linked to nothing
 */
export const userResponsibilityDetailSchema = z
    .object({
    valid_until: z.string().nullable().optional(),
    deadline_at: z.string().optional(),
    unprocessed_count: z.number().int().nonnegative().optional(),
})
    .strict();
/** One thing the user is responsible for. */
export const userResponsibilityItemSchema = z.object({
    kind: z.enum(USER_RESPONSIBILITY_KINDS),
    item_id: z.string().uuid(),
    /** The thing's own name (template / channel / role name, `CODE · title`, the bank, the alias; a DSR's request type). */
    label: z.string(),
    /** False for `bank_connection` / `inbox`: shown as a consequence, never handed over. */
    transferable: z.boolean(),
    /** The `org_role_has_scope` scope a successor needs; null = membership is enough. */
    required_scope: z.string().nullable(),
    detail: userResponsibilityDetailSchema.nullable(),
});
/** A member who may take over — active, not deleted, holding every scope the organization's items need. */
export const userHandoverCandidateSchema = z.object({
    user_id: z.string().uuid(),
    /** `profiles.display_name`; null when unset (label with `email`). */
    name: z.string().nullable(),
    email: z.string().nullable(),
    role: z.string(),
});
/**
 * One section of the handover: an organization (by name), or — with
 * `organization_id: null` and `name: null`, always last — the user-level
 * items (`inbox`). `items` by kind then label; `eligible_successors` sorted
 * by the RPC — case-insensitively by name, else email, then user id — and
 * shown in that order (the web does not re-sort); `[]` when the section has
 * no transferable item (or nobody qualifies).
 */
export const userResponsibilityOrganizationSchema = z.object({
    organization_id: z.string().uuid().nullable(),
    name: z.string().nullable(),
    items: z.array(userResponsibilityItemSchema),
    eligible_successors: z.array(userHandoverCandidateSchema),
});
/** `GET /api/v1/users/:userId/responsibilities` envelope — the RPC's jsonb, parsed. */
export const userResponsibilitiesResponseSchema = z.object({
    organizations: z.array(userResponsibilityOrganizationSchema),
});
//# sourceMappingURL=superAdminUsers.js.map